// Client for the shared-review Durable Object backend.
import type { AssembledContract, ReviewComment, ReviewReply, ReviewStatus, ReviewAttachment, ReviewReceipt } from "../contracts/types";

export interface RemoteReview {
  exists: boolean;
  version: number;
  meta: { title: string; ref: string; createdAt: string };
  contract: AssembledContract | null;
  comments: ReviewComment[];
  attachments?: ReviewAttachment[];
  requestReceipt?: boolean;
  receipt?: ReviewReceipt | null;
}

async function jsonOrThrow(res: Response): Promise<any> {
  if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`);
  return res.json();
}

export async function createReview(payload: { contract: AssembledContract; comments: ReviewComment[]; title: string; ref?: string; createdAt?: string; requestReceipt?: boolean }): Promise<{ id: string; version: number }> {
  return jsonOrThrow(await fetch("/api/review/create", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) }));
}
export async function getReview(id: string): Promise<RemoteReview> {
  return jsonOrThrow(await fetch(`/api/review/${id}`));
}
export async function addRemoteComment(id: string, comment: ReviewComment): Promise<RemoteReview> {
  return jsonOrThrow(await fetch(`/api/review/${id}/comment`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ comment }) }));
}
export async function addRemoteReply(id: string, commentId: string, reply: ReviewReply): Promise<RemoteReview> {
  return jsonOrThrow(await fetch(`/api/review/${id}/reply`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ commentId, reply }) }));
}
export async function setRemoteStatus(id: string, commentId: string, status: ReviewStatus): Promise<RemoteReview> {
  return jsonOrThrow(await fetch(`/api/review/${id}/status`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ commentId, status }) }));
}
export async function deleteRemoteComment(id: string, commentId: string): Promise<RemoteReview> {
  return jsonOrThrow(await fetch(`/api/review/${id}/delete`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ commentId }) }));
}
export async function pushSnapshot(id: string, contract: AssembledContract, title: string): Promise<RemoteReview> {
  return jsonOrThrow(await fetch(`/api/review/${id}/snapshot`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ contract, title }) }));
}
export async function attachToReview(id: string, attachment: ReviewAttachment): Promise<RemoteReview> {
  return jsonOrThrow(await fetch(`/api/review/${id}/attach`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ attachment }) }));
}
export async function detachFromReview(id: string, attachmentId: string): Promise<RemoteReview> {
  return jsonOrThrow(await fetch(`/api/review/${id}/detach`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ attachmentId }) }));
}
/** The payee submits their receipt confirmation (or null to clear it). */
export async function submitReceipt(id: string, receipt: ReviewReceipt | null): Promise<RemoteReview> {
  return jsonOrThrow(await fetch(`/api/review/${id}/receipt`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ receipt }) }));
}
/** The owner turns the receipt request on/off on an existing shared certificate. */
export async function setRequestReceipt(id: string, requestReceipt: boolean, contract?: AssembledContract, title?: string): Promise<RemoteReview> {
  return jsonOrThrow(await fetch(`/api/review/${id}/snapshot`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ requestReceipt, contract, title }) }));
}
